import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

import { addressApi } from '../api/address';

const MIN_QUERY_LENGTH = 3;

export default function AddressAutocompleteField({
  label,
  value,
  onValueChange,
  onSuggestionSelect,
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  placeholder,
  name,
  autoFocus = false,
  size = 'medium',
  fullWidth = true,
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (disabled) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    const query = (inputValue || '').trim();
    if (query.length < MIN_QUERY_LENGTH) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const timerId = window.setTimeout(async () => {
      try {
        const response = await addressApi.suggest({
          query,
          count: 8,
        });
        if (!cancelled) {
          setOptions(Array.isArray(response?.suggestions) ? response.suggestions : []);
        }
      } catch {
        if (!cancelled) {
          setOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 280);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [disabled, inputValue]);

  const selectedOption = useMemo(() => {
    if (!value) {
      return null;
    }
    return options.find((option) => option.value === value) || null;
  }, [options, value]);

  return (
    <Autocomplete
      freeSolo
      fullWidth={fullWidth}
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      disabled={disabled}
      loading={loading}
      filterOptions={(items) => items}
      isOptionEqualToValue={(option, currentValue) => option?.value === currentValue?.value}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option?.value || '';
      }}
      noOptionsText={
        (inputValue || '').trim().length < MIN_QUERY_LENGTH
          ? 'Введите минимум 3 символа'
          : 'Ничего не найдено'
      }
      loadingText="Ищем адреса..."
      onInputChange={(event, nextInputValue, reason) => {
        setInputValue(nextInputValue);
        if (reason !== 'reset') {
          onValueChange?.(nextInputValue);
        }
      }}
      onChange={(event, nextValue) => {
        if (typeof nextValue === 'string') {
          onValueChange?.(nextValue);
          onSuggestionSelect?.(null);
          return;
        }

        const nextAddress = nextValue?.value || '';
        onValueChange?.(nextAddress);
        onSuggestionSelect?.(nextValue || null);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          name={name}
          autoFocus={autoFocus}
          size={size}
          fullWidth={fullWidth}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
